import { Link } from "@circleco/compass/components/Link";
import type { ReactNode } from "react";

import { useEmbedAwareOpenUrl } from "@/react/components/CommentsCompass/useEmbedAwareOpenUrl";
import { useCurrentUser } from "@/react/contexts/punditUserContext";
import type { CSSAnchorAttributes } from "@/react/helpers/cssAnchorHelpers";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { authPathsWithRedirect } from "@/react/helpers/urlHelpers";

interface ActionLinkProps {
  active?: boolean;
  anchorProps?: CSSAnchorAttributes;
  children: ReactNode;
  className?: string;
  dataTestId?: string;
  disabled?: boolean;
  onClick: () => void;
  requiresAuth?: boolean;
}

export const ActionLink = ({
  className,
  children,
  active,
  anchorProps,
  disabled = false,
  requiresAuth = false,
  onClick,
  dataTestId,
}: ActionLinkProps) => {
  const currentUser = useCurrentUser();
  const openUrl = useEmbedAwareOpenUrl();

  const handleClick = (event: React.MouseEvent) => {
    event.preventDefault();
    if (disabled) return;
    if (requiresAuth && !currentUser) {
      openUrl(authPathsWithRedirect.signIn());
      return;
    }
    onClick();
  };

  return (
    <span
      className={classNames("inline-flex", className, {
        "text-brand hover:text-brand-dark": active,
      })}
      {...anchorProps}
    >
      <Link
        asButton
        variant="custom"
        typography={active ? "label-sm" : "body-sm"}
        color={active ? "current" : "secondary"}
        hoverColor={active ? "current" : "primary"}
        data-testid={dataTestId}
        onClick={handleClick}
        aria-disabled={disabled}
        aria-pressed={active}
      >
        {children}
      </Link>
    </span>
  );
};
